import "oc-ui/errorHandling.js";
import ListenerMixin from "oc-commons/ListenerMixin.js";
import Commons from "oc-commons/Commons.js";
import DeviceDetection from "oc-commons/DeviceDetection.js";
import I18N from "oc-I18N";
import DefaultAppSettings from "./DefaultAppSettings.js";
import UndoHandler from "./UndoHandler.js";
import localSettings from "oc-ui/localSettings.js";
import Dialogs from "oc-ui/dialogs/Dialogs";
import LZString from "lz-string";
import Urls from "oc-commons/Urls";
import Wkfg2BackendApi from "oc-app/Wkfg2BackendApi";
import Wkfg2TypeConverter from "oc-app/Wkfg2TypeConverter";
// import "@webcomponents/webcomponentsjs/webcomponents-bundle";
// import "@webcomponents/webcomponentsjs";

// AbstractApp darf keine Referenz auf oc-threejs haben, weil es Anwendungen gibt, die kein THREE verwenden!
//import "oc-threejs/ar/BufferGeometryToIndexed";

import { defaultCatchAsWarning } from "oc-ui/errorHandling";

/**
 * Basis-Klasse für alle Holodeck-Apps.
 * Registriert sich als globales Object APP im Window-Scope.
 * APP wird in Holodeck als globaler Zugriffspunkt für die hier deklarierten Eigenschaften benutzt.
 *
 * Die App sollte folgende Events auf sich selbst auslösen:
 * - **changedConfiguration** immer nachdem die Configuration verändert wurde
 * - **ready** sobald alle initialen Ladevorgänge abgeschlossen sind und der Loading-Dialog ausgeblendet wurde
 *
 * ## Asynchrones Laden
 *
 * Der Ladevorgang einer App ist asynchron. Hier ein typisches Beispiel, wie man
 * seine Modelle und Konfigurationen asynchron lädt:
 *
 * ```
 * // Lädt die Materialien asynchron
 * let loadedMaterials = false;
 * UniversalLoader.loadMTL(modelsPath+'ZAUNPLANER.mtl', (materials)=>{
 *    UniversalLoader.setMaterialFactory(materials);
 *    loadedMaterials = true;
 * });
 *
 * // Lädt die Produkte asynchron
 * this.loadMetadata();
 *
 * Commons.delayUntil(()=>{
 *    // erst jetzt kann man die Konfiguration (und damit 3D-Modelle) laden
 *    this.loadInitialConfiguration();
 * },()=>loadedMaterials && APP.metadata.length>0);
 *
 * this.on('loadedInitialConfiguration', ()=> {
 *    UniversalLoader.delayUntilEverythingLoaded(()=>{
 *       // Blendet den Loading-Screen aus, sobald alles geladen ist
 *       this.hideInitialLoadingDialog();
 *    },'hideInitialLoadingDialog');
 * });
 * ```
 *
 */
export default class AbstractApp extends ListenerMixin() {
    /**
     * name der App
     * @type {string}
     */
    name;

    /**
     * das Wurzel-DOM-Element des Konfigurators
     * @type {*}
     */
    container;

    /**
     * ermöglicht Browser-lokales Überschreiben der Settings
     * @type {Object}
     */
    settingsHandler;

    /**
     * die Einstellparameter der App
     * @type {Object}
     */
    settings;

    /**
     * stellt undo / redo von Configurationsänderungen bereit
     * @type {UndoHandler}
     */
    undoHandler;

    /**
     * die Produkt-Metadaten, kann die Produktliste sein (Planer) oder ein Produkttree (3dkfg)
     * @type {Array<Object>}
     */
    metadata;

    /**
     * die Liste aller Configurationen, die dem User potentiell angezeigt werden
     * @type {Array<Object>}
     */
    configurationList;

    /**
     * die gerade angezeigte Konfiguration
     * @type {?Object}
     */
    configuration;

    /**
     * map der canvas fabric referenzen für den DruckDesigner
     * @type {?Object}
     */
    printDesignerMap;

    /**
     * der User, kann null sein
     * @type {?Object}
     */
    user;

    // ist die App im ready-State?
    ready = false;

    /**
     * @param name interner, eindeutiger Bezeichner der App, wird nicht angezeigt
     * @param container JQuery-Element, in dem der Configurator erstellt wird
     * @param settings Einstellparameter
     */
    constructor(name, container = "body", settings = null) {
        super();

        if (settings && settings.i18n_json) {
            I18N.setKeyValueMap(settings.i18n_json);
        }

        // Convenience. Könnte bei Namenskollisionen mit umgebender Seite mittels string-replace-webpack-plugin ersetzt werden.
        window["APP"] = this;

        this.container = $(container);

        if (this.container.length == 0) {
            throw new Error("App constructed without container element");
        }

        this.name = name;
        this.undoHandler = new UndoHandler();
        this.metadata = [];
        this.configurationList = [];
        this.configuration = null;
        this.user = null;
        this.mergeSettings(settings);

        this.on("ready", () => {
            this.ready = true;
            $("body").addClass("holoAppReady");
        });

        this.checkWebGL();

        if (this.settings.autosaveAndRestore) {
            this.setAutosaveAndRestore();
        }

        this.wkfgBackendApi = this.createWkfgBackendApi();
    }

    /** */
    mergeSettings(settings) {
        this.settingsHandler = localSettings(name + "_settings", settings);
        this.settings = Commons.defaultsDeepSafe({}, this.settingsHandler.getMerge(), DefaultAppSettings);
    }

    /** */
    checkWebGL() {
        if (typeof THREE !== "undefined") {
            if (!DeviceDetection.isWebGLAvailable()) {
                this.noWebGL();
                return false;
            }
        }
    }

    /** */
    noWebGL() {
        alert(I18N("Auf Ihrem System steht leider kein WebGL zur Verfügung."));
    }

    /**
     * Lädt die Configuration in die Stage und führt ein Reset des Arrangement aus
     */
    setStageFromConfiguration(configurationJson = null) { }

    /**
     * Updatet die Konfiguration wobei sich das Json nicht geändert hat. Eine Konfiguration ist ja eine
     * Zeile in der Konfigurationstabelle. Dabei ist für das Arrangement eigentlich nur das Json Feld
     * relevant. Beim Speichern kann der Server einige der anderen Felder aktualisieren, und genau
     * für diesen Anwendungsfall dient diese Methode, bei der kein Update des Arrangement erfolgt.
     */
    updateConfigurationWithUnchangedJson(configurationJson = null) {
        this.configuration = configurationJson;
    }

    /**
     * Serialisiert die Stage als Configuration
     */
    getStageAsConfiguration() {
        return {};
    }

    /**
     * Gibt eine Liste mit Fehlermeldungen zurück, falls das Modell nicht valide ist. Validiert eventuell lazy.
     * Eine typische Meldungen ist z.B. "Der Sicherheitsabstand zwischen den Schaukeln wird nicht eingehalten."
     */
    getStageValidationErrors() {
        return [];
    }

    getCurrentConfiguration() {
        return {};
    }

    /**
     * setzt das Autosave-Feature aktiv, d.h. sollte eine veränderte Konfiguration ungespeichert sein,
     * dann wird der Benutzer beim nächsten Öffnen des Konfigurators gefragt,
     * ob er die ungespeicherte Konfiguration weiter bearbeiten möchte. Basiert auf localStorage.
     */
    setAutosaveAndRestore(eventName = "changedConfiguration") {
        let wasStoredInThisSesssion = false;
        const deleteStoredConfiguration = () => {
            // console.log('delete localStorage.configuration');

            delete localStorage["configuration"];
            delete localStorage["configurationCamera"];
            wasStoredInThisSesssion = false;
        };
        const debouncedSave = _.debounce(() => {
            localStorage["configurationCamera"] = JSON.stringify(STAGE.getCurrentCameraConfig());

            if (APP.isConfigurationChanged()) {
                // console.log('store localStorage.configuration because ',eventName);

                localStorage["configuration"] = LZString.compress(JSON.stringify(APP.getStageAsConfiguration()));
                wasStoredInThisSesssion = true;
            } else if (wasStoredInThisSesssion) {
                deleteStoredConfiguration();
            }
        }, 1000);

        APP.on(eventName,debouncedSave);
        APP.on("savedConfiguration", deleteStoredConfiguration);
        const deregister = ()=>{
            APP.off(eventName,debouncedSave);
            APP.off("savedConfiguration", deleteStoredConfiguration);
        }

        const cameraConfig =
            localStorage["configurationCamera"] == null ? null : JSON.parse(localStorage["configurationCamera"]);
        if (localStorage["configuration"] != null) {
            try {
                const storedConfiguration = JSON.parse(LZString.decompress(localStorage["configuration"]));
                APP.onOnce("loadedInitialConfiguration", () => {
                    if (!APP.settings.autosaveAndRestore){
                        deregister();
                        return;
                    }
                    // hier warten wir noch mal 2 Sekunden, weil einige Apps wie Zaunplaner die Konfiguration nach dem Laden
                    // noch mal aktualisieren und transiente Parameter hinzugefügt werden
                    _.delay(() => {
                        try {
                            // hier prüfen wir, ob die aktuelle Konfiguration wirklich unterschiedlich zu der aus dem localStorage ist,
                            // um den User nicht zu nerven, falls die autosave-Konfiguration gleich der geladenen ist
                            // hier interessiert nur der Json-Teil, weil der Rest reine Meta-Information ist und sich da Felder ändern können, z.B. last_access
                            let storedJson = storedConfiguration.Json;
                            let currentJson = APP.getStageAsConfiguration().Json;
                            if (!Commons.isEqualEpsilon(storedJson, currentJson)) {
                                console.log("found an unsaved localStorage.configuration");

                                const autoOK = APP.settings.userRole == "admin";
                                const restore = () => {
                                    this.setStageFromConfiguration(storedConfiguration);
                                    if (cameraConfig) {
                                        _.delay(() => {
                                            STAGE.setCameraConfig(cameraConfig);
                                        }, 500);
                                    }
                                    this._setConfigurationCookie();
                                    APP.trigger("changedConfiguration");
                                    APP.trigger("loadedConfiguration");
                                    this._setConfigurationSnapshot();
                                };

                                if (autoOK && _.isEmpty(Urls.urlParam("Code"))) {
                                    Dialogs.showToast(
                                        I18N("Weil sie als Administrator eingeloggt sind, wurde die"),
                                        1500
                                    );
                                    _.delay(restore, 300);
                                } else {
                                    Dialogs.askQuestion(
                                        I18N("Wir haben eine Konfiguration im Speicher gefunden. Möchten"),
                                        [
                                            { label: I18N("Ja"), primary: true, action: restore },
                                            { label: I18N("Nein"), action: deleteStoredConfiguration },
                                        ]
                                    );
                                }
                            }
                        } catch (e) {
                            console.warn(e);
                        }
                    }, 2000);
                });
            } catch (e) {
                console.warn(e);
            }
        }
    }

    createWkfgBackendApi() {
        return new Wkfg2BackendApi(this.settings.wkfgBackendUrl, this.createWkfgTypeConverter());
    }

    createWkfgTypeConverter() {
        return new Wkfg2TypeConverter();
    }

    // Funktion für fotorealistisches Rendering
    // Kümmert sich um GLB-Export und Kommunikation mit dem WKFG
    // Das WKFG kommuniziert anschließend mit der ServiceAPI (zentraler neuer Server) und speichert asynchron die gerenderten Bilder
    // Im Anschluss wird die WKFG-Route "mail" aufgerufen, da die Renderings typischerweise in der Mail enthalten sind
    /**
     *
     * @param {String} projectName Projektname, sollte in als default APP.name aber gesetzt sein
     * @param {String} filename Kann gesetzt werden, default reicht aber
     * @param {Object3d} object3d Ist das o3, welches Exportiert und gerendert wird. Sollte Kameras (die werden gerendert) und Lichter enthalten!
     * @param {String} code Muss den aktuellen Konfigurationscode enthalten
     * @param {Function} before Wird vor dem Export ausgeführt. Nutzbar für Dinge wie: Boxes aus o3 entfernen -> Keine Sorge, es wird mit einem deepClone gearbeitet, man manipuliert nicht die Szene auch wenn object3d = STAGE.scene
     * @param {Function} after Wird nach dem Export ausgeführt
     * @param {Boolean} hideknown Flag um bekannte Objektproblematiken automatisch zu beheben: z.B. THREE.LineSegments
     * @param {Array} instancesToBeHidden Ein Array mit Klassen-Objekten. Alle im o3 auffindbaren instanzen dieser Klassen werden im Rendering ausgeblendet
     * @param {Object} renderData Das Objekt beschreibt Rendereigenschaften die durch Blender berückstichtigt werden: Verpflichtend ist der name mit .json am Ende (sorry for that, siehe default).
     * @param {Number} multipart Ermöglicht upload von meheren GLB's: Pipeline triggert Mail erst nach letztem Erfolg. Hier Anzahl der gesamten GLBs angeben
     * @param {Number} part Positionsangabe innerhalb multipart
     */
    fotorealisticRender({
        projectName = APP.name ? APP.name : null,
        filename = "render.glb",
        object3d = null,
        code = null,
        before = null,
        after = null,
        hideknown = true,
        instancesToBeHidden = [],
        renderData = { name: "renderData.json" },
        multipart = 1,
        part = 1,
        useRenderfarm = false,
        blendFilename = "auto.blend"
    } = {}) {
        if (!projectName || !object3d || !code || !projectName.includes("_")) {
            throw new Error("MISSING OR INCORRECT PARAMS FOR FOTOREALISTIC RENDER");
        }
        if(!projectName.includes("_")) {
            throw new Error("THE PROJECTNAME NEEDS TO INCLUDE A _ SYMBOL: e.g.: ocschrank_oc")
        }
        renderData = {hdriUrl: "", cameraCount: 1, ...renderData};
        object3d = _.cloneDeep(object3d);
        return defaultCatchAsWarning(
            new Promise((resolve, reject) => {
                this.prepareO3ForExport(object3d, hideknown, instancesToBeHidden);
                if (before) before(object3d);
                console.log(object3d.scale);
                STAGE.gltfToBlob(
                    (blob) => {
                        const renderDataJSON = {
                            fileName: renderData.name,
                            blob: new Blob([JSON.stringify(renderData)], { type: "application/json" }),
                        };
                        this.wkfgBackendApi.uploadContent(`Konfiguration/${code}`, renderDataJSON)
                            .then(() => {
                                const glbUpload = {
                                    fileName: filename,
                                    blob: blob,
                                };
                                this.wkfgBackendApi.fotorealisticRenderAPI(
                                    `Konfiguration/${code}`,
                                    glbUpload,
                                    projectName,
                                    multipart,
                                    part,
                                    useRenderfarm,
                                    blendFilename
                                ).then(() => { resolve(); }).catch(e => reject(e));
                            }).catch(e => reject(e));
                    },
                    { binary: true, googleARUVMapping: false },
                    object3d
                );
                if (after) after(object3d);
            })
        );
    }

    // Funktion für AR: Allerdings kein Aufruf
    // Diese Funktion kümmert sich darum, dass das WKFG die richtigen Ressourcen für AR bereitstellt
    // Wenn nötig, wird eine USDZ konvertiert und abgelegt
    // Bei jedem Aufruf wird der glb Name entsprechend manipuliert, um das Cachen des Google-Scene Viewers zu vermeiden
    /**
     * @param {String} projectName Projektname, sollte in als default APP.name aber gesetzt sein
     * @param {Boolean} ARUvMapping Sollte true sein, das UV-Mapping des GLTFExporters wird damit gefixt
     * @param {Boolean} convertToUSDZ Soll die zu speichernde GLB zu einer USDZ Konvertiert werden?
     * @param {Object3d} object3d Ist das o3, welches Exportiert und in AR Dargestellt wird.
     * @param {String} code Muss den aktuellen Konfigurationscode enthalten
     * @param {Function} before Wird vor dem Exportieren ausgeführt. Oftmals benötigt zum ausblenden unbekannter Objekte im Zusammenhang mit GLB / GLTF
     * @param {Function} after Wird nach dem Exportieren ausgeführt.
     * @param {Boolean} hideknown Flag um bekannte Objektproblematiken automatisch zu beheben: z.B. THREE.LineSegments
     * @param {Array} instancesToBeHidden Ein Array mit Klassen-Objekten. Alle im o3 auffindbaren instanzen dieser Klassen werden im Rendering ausgeblendet
    */
    augmentedReality({
        projectName = APP.name ? APP.name : null,
        ARUvMapping = null,
        convertToUSDZ = null,
        object3d = null,
        code = null,
        before = null,
        after = null,
        hideknown = true,
        instancesToBeHidden = [],
    } = {}) {
        if (!projectName || !object3d || !code) {
            throw new Error("MISSING OR INCORRECT PARAMS FOR FOTOREALISTIC RENDER");
        }
        object3d = _.cloneDeep(object3d);
        return defaultCatchAsWarning(
            new Promise((resolve, reject) => {
                this.getNextArFileIndex(code).then((index) => {
                    this.prepareO3ForExport(object3d, hideknown, instancesToBeHidden);
                    const filename = `ar${index}.glb`;
                    if (before) before(object3d);
                    STAGE.gltfToBlob(
                        (blob) => {
                            const glbUpload = {
                                fileName: filename,
                                blob: blob,
                            };
                            this.wkfgBackendApi.augmentedRealityAPI(
                                `Konfiguration/${code}`,
                                glbUpload,
                                projectName,
                                convertToUSDZ
                            ).then(() => { resolve(); }).catch(e => reject(e));
                        },
                        { binary: true, googleARUVMapping: ARUvMapping },
                        object3d
                    );
                    if (after) after(object3d);
                });
            })
        );
    }

    // Ernittelt alle Dateien die ar enthalten und ermittelt den nächsten freien index zum anhängen
    // Zum vermeiden des Cachings durch Google bei AR
    getNextArFileIndex(code) {
        return defaultCatchAsWarning(
            new Promise((resolve, reject) => {
                this.wkfgBackendApi
                    .listContent("Konfiguration/" + code)
                    .then((list) => {
                        let names = [];
                        list.forEach((e) => {
                            const from = e.lastIndexOf("/") + 1;
                            const to = e.lastIndexOf(".");
                            names.push(e.slice(from, to));
                        });
                        names = names.filter((n) => n.includes("ar"));
                        resolve(names.length);
                    });
            })
        );
    }

    // Die Funktionen rund um AR und Rendering bieten Mechanismen um Objekte vom Export auszuschließen
    prepareO3ForExport(object3d, hideknown, instancesToBeHidden) {
        if (hideknown) {
            this.changeVisibilityForClass(
                THREE.LineSegments,
                false,
                object3d
            );
        }
        instancesToBeHidden.forEach((i) => {
            this.changeVisibilityForClass(i, false, object3d);
        });
    }

    // Helper for fotorealistic Rendering
    changeVisibilityForClass(classname, bool, object3d) {
        object3d.traverse((o3) => {
            if (o3 instanceof classname) {
                if (!bool && o3.visible) {
                    o3.visible = bool;
                } else {
                    o3.visible = bool;
                }

                if (o3.material) {
                    if (bool && o3.material.visible) {
                        o3.material.visible = bool;
                    } else {
                        o3.material.visible = bool;
                    }
                }
            }
        });
    }

    // Helper for fotorealistic Rendering
    changeVisibilityForInstance(instance, bool) {
        instance.visible = bool;
    }

}
